import defaultMatrix from "./FMS.Capabilities.default.json";
import { FMSType } from "./FMS.Interfaces";

/**
 * The object types the capability matrix declares capabilities for.
 * These map to the existing per-object interfaces (Lead / Unit / Tenant / Lease)
 * but are enumerated here as a set so the matrix and resolver can iterate them.
 * The grain is per-FMS, per-object-type — capabilities differ by object within one
 * FMS (a changes API may cover leases but not leads).
 */
export enum FMSObjectType {
  Lead = "Lead",
  Unit = "Unit",
  Tenant = "Tenant",
  Lease = "Lease"
}

/**
 * The capabilities declared per FMS x object type row.
 * (Rate limiting is NOT a per-object capability — the per-FMS safety ceiling is a
 * separate scalar owned by the FMS API gateway, declared elsewhere.)
 */
export enum FMSCapabilityKey {
  FullPull = "fullPull",
  ChangesApi = "changesApi",
  Webhooks = "webhooks",
  JitFetch = "jitFetch",
  UpdatedAt = "updatedAt",
  /** Append a free-text note to this object in the FMS (Tenant: `appendTenantNotes`; Lead: no implementation yet). ENG-3216. */
  NoteWrite = "noteWrite",
  /** Lead only: this FMS's leads can be judged as abandoned carts (`isAbandonedCartCandidateLead`). ENG-3216. */
  AbandonedCartCandidacy = "abandonedCartCandidacy",
  /** Lease only: raw FMS lease statuses (Overlock/Auction) gate past-due outreach and can be opted back in (PROD-559). ENG-3216. */
  InactiveLeaseStatusOptIn = "inactiveLeaseStatusOptIn",
  // ENG-3416: the pre-send JIT refresh also pulls the location's changes feed (a conversion
  // recorded as a separate record). Meaningful on the Lead row only.
  JitChangesRefresh = "jitChangesRefresh"
}

/**
 * The four states a capability cell can hold. The system reads only `Implemented`;
 * the rest are informational. Modeled as a string-literal union (not a TS enum) so
 * the values type-check directly against the hand-edited JSON source.
 *
 * - `TBD`         — discovery pending; we haven't verified this cell yet. The default.
 * - `None`        — investigated; the FMS does not support this.
 * - `Available`   — the FMS can do it, but we haven't wired it (roadmap).
 * - `Implemented` — wired and drives behavior today. The only state the system acts on.
 */
export const FMSCapabilityState = {
  TBD: "tbd",
  None: "none",
  Available: "avail",
  Implemented: "impl"
} as const;

export type FMSCapabilityState = (typeof FMSCapabilityState)[keyof typeof FMSCapabilityState];

/**
 * A single capability cell. `state` drives everything; `note` is optional human
 * context surfaced in the admin view (e.g. an `impl*` caveat).
 */
export interface FMSCapabilityCell {
  state: FMSCapabilityState;
  note?: string;
}

export type FMSCapabilityRow = Record<FMSCapabilityKey, FMSCapabilityCell>;
export type FMSObjectCapabilities = Record<FMSObjectType, FMSCapabilityRow>;

/**
 * The FMS Capability Matrix: the single source of truth for what each FMS can do.
 * Lives as a static JSON const (not the DB, not UI-editable) — changing it is a PR + deploy.
 *
 * `Partial` because not every `FMSType` member is a real provider in the matrix
 * (e.g. the `StorageMart` and `DoorSwap` enum members are excluded).
 */
export type FMSCapabilityMatrix = Partial<Record<FMSType, FMSObjectCapabilities>>;

// Raw shape of the imported JSON. TypeScript widens JSON string values to `string`, so
// the file can't be typed directly as `FMSCapabilityMatrix` (whose `state` is a literal
// union). `parseMatrix` validates the raw JSON at module load and narrows it.
type RawMatrix = Record<string, Record<string, Record<string, { state: string; note?: string }>>>;

const VALID_STATES = new Set<string>(Object.values(FMSCapabilityState));

/**
 * Validates the hand-edited JSON at module load, then narrows it to the typed matrix.
 * Fail-fast on a malformed edit — a missing object type / capability cell, or an invalid
 * `state` value — because this matrix is the source of truth every sync path reads.
 */
function parseMatrix(raw: RawMatrix): FMSCapabilityMatrix {
  for (const [fms, objects] of Object.entries(raw)) {
    for (const objectType of Object.values(FMSObjectType)) {
      const row = objects[objectType];
      if (!row) throw new Error(`FMS Capability Matrix: ${fms} is missing object type "${objectType}"`);
      for (const capability of Object.values(FMSCapabilityKey)) {
        const cell = row[capability];
        if (!cell) throw new Error(`FMS Capability Matrix: ${fms}.${objectType} is missing capability "${capability}"`);
        if (!VALID_STATES.has(cell.state)) {
          throw new Error(`FMS Capability Matrix: invalid state "${cell.state}" at ${fms}.${objectType}.${capability}`);
        }
      }
    }
  }
  return raw as FMSCapabilityMatrix;
}

/**
 * The matrix. Values are hand-edited in `FMS.Capabilities.default.json` and validated
 * here as the typed source of truth. Most cells start at `tbd` (discovery pending) and
 * are flipped to `none` / `avail` / `impl` as each FMS is discovered and built — StorEdge
 * rows from the ENG-2718/2719/2720 findings first.
 */
export const FMS_CAPABILITY_MATRIX: FMSCapabilityMatrix = parseMatrix(defaultMatrix);
